import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useUiStore } from '../stores/uiStore';
import './SchedulePage.css';

// Schedule tab: the calendar planner (Chunk W7, 2026-09-27). D1 only, through
// /api/calendar. Only game-ready Game Words take a date (Gate 4, enforced in
// the worker). One word per date, one date per word. A date can be cleared
// until it has passed. "Propose week" is rules-based (no model call); nothing
// is written until Odin approves the week. Replaces the Word Binder's Cal view.

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const RUNWAY_AMBER = 14;

interface Readiness {
  content: boolean;
  related_linked: number;
  related_approved: number;
  social: 'built' | 'not built';
  posts: number;
  game_ready: boolean;
  fully_ready: boolean;
}

interface CalWord {
  id: string;
  word: string;
  theme: string | null;
  category: string | null;
  difficulty: number | null;
  teaser: boolean;
  readiness?: Readiness;
}

interface CalDay {
  date: string;
  season: string;
  past: boolean;
  sponsor: null;
  word: CalWord | null;
  status: 'draft' | 'approved' | null;
  readiness: Readiness | null;
  can_remove?: boolean;
}

interface CalendarResponse {
  today: string;
  from: string;
  to: string;
  runway: number;
  social_built?: number;
  days: CalDay[];
}

interface ProposedDay {
  date: string;
  season: string;
  word: CalWord | null;
  why: string;
}

interface Proposal {
  week: { from: string; to: string };
  days: ProposedDay[];
  pool: CalWord[];
  message: string;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function weekdayIndex(date: string): number {
  return (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
}

function shortDate(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function ReadinessMarks({ r }: { r: Readiness }) {
  return (
    <div className="sched-marks">
      <span className={`sched-mark ${r.content ? 'ok' : 'no'}`} title={r.content ? 'Content approved' : 'Content not approved'}>
        Content {r.content ? '✓' : '✗'}
      </span>
      <span className={`sched-mark ${r.social === 'built' ? 'ok' : 'wait'}`} title={r.social === 'built' ? `${r.posts} social posts built` : 'Scheduled, posts not built'}>
        Social {r.social === 'built' ? '✓' : 'not built'}
      </span>
    </div>
  );
}

export default function SchedulePage() {
  const openContentWord = useUiStore((s) => s.openContentWord);
  const [month, setMonth] = useState<string | null>(null);
  const [cal, setCal] = useState<CalendarResponse | null>(null);
  const [ready, setReady] = useState<CalWord[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [picked, setPicked] = useState<CalWord | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});

  const load = useCallback(async (m: string | null) => {
    setLoading(true);
    try {
      const q = m ? `?month=${m}` : `?month=${new Date().toISOString().slice(0, 7)}`;
      const [c, r] = await Promise.all([
        api.get(`/api/calendar${q}`) as Promise<CalendarResponse>,
        api.get('/api/calendar/ready') as Promise<{ words: CalWord[] }>,
      ]);
      setCal(c);
      setReady(r.words || []);
      if (!m) setMonth(c.today.slice(0, 7));
    } catch (err) {
      setNotice({ ok: false, text: (err as Error).message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(month); }, [load, month]);

  const place = async (date: string, wordId: string) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = (await api.put(`/api/calendar/${date}`, { word_id: wordId })) as { message: string };
      setNotice({ ok: true, text: r.message });
      setPicked(null);
      await load(month);
    } catch (err) {
      setNotice({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (day: CalDay) => {
    if (busy || !day.word) return;
    if (!window.confirm(`Take "${day.word.word}" off ${shortDate(day.date)}?`)) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = (await api.delete(`/api/calendar/${day.date}`)) as { message: string };
      setNotice({ ok: true, text: r.message });
      await load(month);
    } catch (err) {
      setNotice({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const propose = async () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const p = (await api.post('/api/calendar/propose', {})) as Proposal;
      setProposal(p);
      const c: Record<string, string> = {};
      for (const d of p.days) c[d.date] = d.word?.id || '';
      setChoices(c);
    } catch (err) {
      setNotice({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const approveWeek = async () => {
    if (!proposal || busy) return;
    const days = proposal.days.map((d) => ({ date: d.date, word_id: choices[d.date] })).filter((d) => d.word_id);
    if (!days.length) return;
    if (!window.confirm(`Approve ${days.length} date${days.length === 1 ? '' : 's'} for the week of ${shortDate(proposal.week.from)}?`)) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = (await api.post('/api/calendar/approve-week', { days })) as { message: string };
      setNotice({ ok: true, text: r.message });
      setProposal(null);
      setMonth(proposal.week.from.slice(0, 7));
      await load(proposal.week.from.slice(0, 7));
    } catch (err) {
      setNotice({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  // Mon-first grid with blank lead cells.
  const cells = useMemo(() => {
    if (!cal) return [] as (CalDay | null)[];
    const lead = cal.days.length ? weekdayIndex(cal.days[0].date) : 0;
    const out: (CalDay | null)[] = Array.from({ length: lead }, () => null);
    out.push(...cal.days);
    while (out.length % 7) out.push(null);
    return out;
  }, [cal]);

  const chosenIds = new Set(Object.values(choices).filter(Boolean));
  const runway = cal?.runway ?? 0;
  const socialBuilt = cal?.social_built ?? 0;
  const runwayClass = runway === 0 ? 'red' : runway < RUNWAY_AMBER ? 'amber' : 'green';

  return (
    <div className="schedule-page">
      <header className="sched-header">
        <div className="sched-title-row">
          <h1 className="sched-title">Schedule</h1>
          <span className={`sched-runway sched-runway-${runwayClass}`} title="Consecutive game-ready days from today: a Game Word with approved content. Social posts are not part of runway.">
            Runway: {runway} day{runway === 1 ? '' : 's'}
          </span>
          <span className="sched-social-built" title="Consecutive days from today that are game-ready and also have social posts built">
            Social built: {socialBuilt} day{socialBuilt === 1 ? '' : 's'}
          </span>
        </div>
        <div className="sched-controls">
          <button className="sched-btn" onClick={() => month && setMonth(shiftMonth(month, -1))} disabled={!month || loading}>‹</button>
          <span className="sched-month">{month ? monthLabel(month) : ''}</span>
          <button className="sched-btn" onClick={() => month && setMonth(shiftMonth(month, 1))} disabled={!month || loading}>›</button>
          <button className="sched-btn" onClick={() => cal && setMonth(cal.today.slice(0, 7))} disabled={loading}>Today</button>
          <button className="sched-btn sched-btn-primary" onClick={propose} disabled={busy}>Propose week</button>
        </div>
      </header>

      {notice && <div className={`sched-notice ${notice.ok ? 'ok' : 'fail'}`} role="status">{notice.text}</div>}

      {proposal && (
        <section className="sched-proposal">
          <div className="sched-proposal-head">
            <strong>Proposed week: {shortDate(proposal.week.from)} to {shortDate(proposal.week.to)}</strong>
            <span className="sched-muted">{proposal.message} Rules-based spread by theme, difficulty and season. No model call.</span>
          </div>
          <div className="sched-proposal-days">
            {proposal.days.map((d) => (
              <div key={d.date} className="sched-proposal-day">
                <div className="sched-proposal-date">{shortDate(d.date)} <span className="sched-season">{d.season}</span></div>
                <select
                  value={choices[d.date] || ''}
                  onChange={(e) => setChoices((c) => ({ ...c, [d.date]: e.target.value }))}
                  disabled={busy}
                >
                  <option value="">(leave open)</option>
                  {proposal.pool.map((w) => (
                    <option key={w.id} value={w.id} disabled={chosenIds.has(w.id) && choices[d.date] !== w.id}>
                      {w.word}{w.difficulty ? ` · d${w.difficulty}` : ''}{w.theme ? ` · ${w.theme.split(',')[0]}` : ''}
                    </option>
                  ))}
                </select>
                {d.word && choices[d.date] === d.word.id && <div className="sched-muted sched-why">{d.why}</div>}
              </div>
            ))}
          </div>
          <div className="sched-proposal-actions">
            <button className="sched-btn sched-btn-primary" onClick={approveWeek} disabled={busy || chosenIds.size === 0}>Approve week</button>
            <button className="sched-btn" onClick={() => setProposal(null)} disabled={busy}>Cancel</button>
          </div>
        </section>
      )}

      <div className="sched-body">
        <div className="sched-grid-wrap">
          <div className="sched-grid">
            {WEEKDAYS.map((w) => <div key={w} className="sched-weekday">{w}</div>)}
            {cells.map((day, i) => {
              if (!day) return <div key={`blank-${i}`} className="sched-cell sched-cell-blank" />;
              const isToday = cal?.today === day.date;
              const canDrop = !day.past && !day.word;
              return (
                <div
                  key={day.date}
                  className={`sched-cell ${day.past ? 'past' : ''} ${isToday ? 'today' : ''} ${dragOver === day.date ? 'drag-over' : ''} ${picked && canDrop ? 'pickable' : ''}`}
                  onDragOver={(e) => { if (canDrop) { e.preventDefault(); setDragOver(day.date); } }}
                  onDragLeave={() => setDragOver((d) => (d === day.date ? null : d))}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(null);
                    const id = e.dataTransfer.getData('text/plain');
                    if (canDrop && id) void place(day.date, id);
                  }}
                  onClick={() => { if (picked && canDrop) void place(day.date, picked.id); }}
                >
                  <div className="sched-cell-top">
                    <span className="sched-daynum">{Number(day.date.slice(8))}</span>
                    <span className="sched-season">{day.season}</span>
                  </div>
                  {day.word ? (
                    <>
                      <button className="sched-word" onClick={(e) => { e.stopPropagation(); openContentWord(day.word!.id); }} title="Open on the Content tab">
                        {day.word.word}
                        {day.word.teaser && <span className="sched-tag">teaser</span>}
                      </button>
                      {day.readiness && <ReadinessMarks r={day.readiness} />}
                      {day.can_remove && (
                        <button className="sched-remove" onClick={(e) => { e.stopPropagation(); void remove(day); }} disabled={busy} title="Take off this date">
                          Remove
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="sched-empty">{day.past ? '' : picked ? `Put ${picked.word} here` : 'Empty'}</span>
                  )}
                  <div className="sched-sponsor" title="Sponsor slots come later">Sponsor: open</div>
                </div>
              );
            })}
          </div>
          {loading && <div className="sched-muted">Loading…</div>}
        </div>

        <aside className="sched-side">
          <h2 className="sched-side-title">Game-ready words ({ready.length})</h2>
          <p className="sched-muted">Drag a word onto a date, or click it and then click a date.</p>
          {ready.length === 0 && !loading && (
            <p className="sched-muted">No game-ready Game Words without a date. A Game Word is ready once its content and its three Related Words are approved on the Content tab.</p>
          )}
          <div className="sched-ready-list">
            {ready.map((w) => (
              <button
                key={w.id}
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/plain', w.id)}
                className={`sched-ready ${picked?.id === w.id ? 'picked' : ''}`}
                onClick={() => setPicked((p) => (p?.id === w.id ? null : w))}
              >
                <span className="sched-ready-word">{w.word}{w.teaser && <span className="sched-tag">teaser</span>}</span>
                <span className="sched-muted">
                  {w.difficulty ? `difficulty ${w.difficulty}` : 'no difficulty'}{w.theme ? ` · ${w.theme.split(',')[0]}` : ''}
                </span>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
